<template>
  <div class="vendor-selector">
    <label v-if="label" :for="id" class="selector-label">{{ label }}</label>
    <div class="selector-wrapper" ref="selectorRef">
      <div
        class="selector-input"
        :class="{ 'open': isOpen, 'disabled': disabled || loading || !companyId }"
        @click="toggleDropdown"
      >
        <input
          v-if="isOpen"
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="placeholder"
          @keydown.esc="closeDropdown"
          @keydown.enter.prevent="selectFirst"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
        />
        <div v-else class="selected-value" @click.stop="toggleDropdown">
          {{ selectedVendorName || placeholder }}
        </div>
        <LineIcon
          name="chevron-down"
          :size="11"
          :stroke-width="1.8"
          class="dropdown-icon"
          :class="{ 'rotate': isOpen }"
          @click.stop="toggleDropdown"
        />
      </div>

      <div v-if="isOpen" class="dropdown-menu">
        <div v-if="loading" class="dropdown-item loading">
          <span class="spinner-small"></span>
          Loading vendors...
        </div>
        <div v-else-if="!companyId" class="dropdown-item empty">
          Select a company first
        </div>
        <div v-else-if="filteredVendors.length === 0" class="dropdown-item empty">
          No vendors found
        </div>
        <div v-else class="dropdown-list">
          <div
            class="dropdown-item clear-option"
            :class="{
              'selected': !selectedVendor,
              'highlighted': highlightedIndex === -1
            }"
            @click="clearSelection"
            @mouseenter="highlightedIndex = -1"
          >
            <div class="vendor-name">All Vendors</div>
          </div>
          <div
            v-for="(vendor, index) in filteredVendors"
            :key="vendor.id"
            class="dropdown-item"
            :class="{
              'selected': vendor.id === selectedVendor,
              'highlighted': index === highlightedIndex
            }"
            @click="selectVendor(vendor)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="vendor-name">{{ vendor.vendor_name }}</div>
            <div v-if="vendor.vendor_vat" class="vendor-vat">{{ vendor.vendor_vat }}</div>
          </div>
        </div>
      </div>
    </div>
    <span v-if="error" class="selector-error">{{ error }}</span>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { useVendors } from '@/composables/useCompanyVendor';

const props = defineProps({
  modelValue: {
    type: [Number, String, null],
    default: null
  },
  companyId: {
    type: [Number, String, null],
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select Vendor'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  id: {
    type: String,
    default: 'vendor-selector'
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const companyIdRef = computed(() => props.companyId);
const { vendors, loading, error, loadVendors } = useVendors(companyIdRef);

const isOpen = ref(false);
const searchQuery = ref('');
const selectorRef = ref(null);
const searchInput = ref(null);
const highlightedIndex = ref(0);

const selectedVendor = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const selectedVendorName = computed(() => {
  if (!vendors.value || vendors.value.length === 0) return '';
  const vendor = vendors.value.find(v => v.id === selectedVendor.value);
  return vendor ? vendor.vendor_name : '';
});

const filteredVendors = computed(() => {
  if (!vendors.value) return [];

  if (!searchQuery.value) {
    return vendors.value;
  }

  const query = searchQuery.value.toLowerCase();
  return vendors.value.filter(vendor => {
    const nameMatch = vendor.vendor_name?.toLowerCase().includes(query);
    const vatMatch = vendor.vendor_vat?.toLowerCase().includes(query);
    return nameMatch || vatMatch;
  });
});

const toggleDropdown = () => {
  if (props.disabled || loading.value || !props.companyId) return;

  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    searchQuery.value = '';
    highlightedIndex.value = 0;
    nextTick(() => {
      searchInput.value?.focus();
    });
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
  highlightedIndex.value = 0;
};

const selectVendor = (vendor) => {
  selectedVendor.value = vendor.id;
  emit('change', vendor);
  closeDropdown();
};

const clearSelection = () => {
  selectedVendor.value = null;
  emit('change', null);
  closeDropdown();
};

const selectFirst = () => {
  if (filteredVendors.value.length > 0) {
    selectVendor(filteredVendors.value[0]);
  }
};

const navigateDown = () => {
  if (highlightedIndex.value < filteredVendors.value.length - 1) {
    highlightedIndex.value++;
    scrollToHighlighted();
  }
};

const navigateUp = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
    scrollToHighlighted();
  }
};

const scrollToHighlighted = () => {
  nextTick(() => {
    const highlighted = document.querySelector('.dropdown-item.highlighted');
    if (highlighted) {
      highlighted.scrollIntoView({ block: 'nearest' });
    }
  });
};

const handleClickOutside = (event) => {
  if (selectorRef.value && !selectorRef.value.contains(event.target)) {
    closeDropdown();
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

// Reset vendor selection when company changes
watch(() => props.companyId, (newCompanyId, oldCompanyId) => {
  if (newCompanyId !== oldCompanyId) {
    selectedVendor.value = null;
    closeDropdown();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Expose methods for parent components
defineExpose({
  loadVendors,
  vendors
});
</script>

<style scoped>
.vendor-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selector-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-dark);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.selector-wrapper {
  position: relative;
}

.selector-input {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 40px 10px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 42px;
}

.selector-input:hover:not(.disabled) {
  border-color: var(--gray-light);
}

.selector-input.open {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.vendor-selector.active-filter .selector-input:not(.open) {
  border: 2px solid #0ea5e9;
  background: rgba(14, 165, 233, 0.05);
}

.selector-input.disabled {
  background: var(--bg-gray);
  cursor: not-allowed;
  opacity: 0.6;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--text-primary);
  background: transparent;
  padding: 0;
}

.search-input::placeholder {
  color: var(--text-light);
}

.selected-value {
  flex: 1;
  font-size: 14px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  position: absolute;
  right: 14px;
  flex-shrink: 0;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
  font-size: 12px;
  pointer-events: none;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 300px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-list {
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--gray-lighter);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: var(--primary-light);
}

.dropdown-item.selected {
  background: var(--primary-light);
  font-weight: 600;
}

.dropdown-item.clear-option {
  font-style: italic;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border);
}

.dropdown-item.clear-option.selected {
  font-style: normal;
  font-weight: 600;
  color: var(--text-primary);
}

.dropdown-item.loading,
.dropdown-item.empty {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  color: var(--text-secondary);
  cursor: default;
  padding: 16px;
}

.dropdown-item.loading:hover,
.dropdown-item.empty:hover {
  background: white;
}

.vendor-name {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.vendor-vat {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.selector-error {
  font-size: 12px;
  color: var(--danger);
  font-style: italic;
}
</style>
